import { useEffect, useRef, memo, useState, useMemo, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Trash2,
  Users,
  Loader2,
  Check,
  AlertCircle,
  Download,
  Upload,
  ChevronDown,
  TrendingUp,
  Zap,
  Layers,
} from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import { IconButton } from '@lean-agility/design-system/react';
import { Input } from '@lean-agility/design-system/react';
import { StepperInput, Label, MenuItem } from '@/components/atoms';
import { ColorPicker, Dropdown, Menu, ThroughputCharts } from '@/components/molecules';
import { DeliverableForm, type BlockingDeliverable } from './DeliverableForm';
import { DeliverablesSetup, TeamDataSourcePicker, ThroughputSetup, useTeamThroughputSource } from '@/features/tracker';
import { getTeamColor, getAllDeliverableOptions, type DeliverableOption } from '@/lib/utils';
import { anchorAbove } from '@/lib/ordering';
import {
  deleteDeliverable,
  deleteSpecificDependency,
  postDeliverableMove,
  exportTeamSheet,
  importTeamSheet,
  patchTeamSheet,
  downloadBlob,
  updateTeam,
} from '@/lib/api';
import type { SimulationFormValues } from '@/types/api';
import { useMediaQuery, useErrorHandler, useAutoSave } from '@/hooks';
import { useSyncedFields } from '@/hooks/formSyncEvents';
import { parseThroughputData, syncedTeamFields, teamAutoSaveFields } from './teamAutoSave';
import { useSyncedFieldArray } from './syncedFieldArray';
import { useWriteErrors } from '@/hooks/useWriteErrors';
import { useInvalidateSimulation } from '@/hooks/queries';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

interface TeamFormProps {
  teamIndex: number;
  onRemove: () => void;
  canRemove: boolean;
  simulationId?: string | null;
  onRefresh?: () => Promise<void>;
  /**
   * Callback when a dependency chip is clicked (for navigation).
   */
  onDependencyClick?: (deliverableId: string, teamName: string) => void;
  /**
   * ID of the deliverable to highlight (for navigation feedback).
   */
  highlightedDeliverableId?: string | null;
  /**
   * Whether this team tab is currently active/visible.
   * Used to trigger virtualizer remeasurement when tab becomes visible.
   */
  isActive?: boolean;
}

export const TeamForm = memo(function TeamForm({
  teamIndex,
  onRemove,
  canRemove,
  simulationId,
  onRefresh,
  onDependencyClick,
  highlightedDeliverableId,
  isActive = true,
}: TeamFormProps) {
  const methods = useFormContext<SimulationFormValues>();
  const {
    register,
    formState: { errors },
    control,
    getValues,
    setValue,
  } = methods;
  const [isExporting, setIsExporting] = useState(false);
  const [isImportingReplace, setIsImportingReplace] = useState(false);
  const [isImportingMerge, setIsImportingMerge] = useState(false);
  const fileInputReplaceRef = useRef<HTMLInputElement>(null);
  const fileInputMergeRef = useRef<HTMLInputElement>(null);

  // Get team name for error context
  const watchedName = useWatch({ control, name: `teams.${teamIndex}.name` });
  const { handleError, handleSuccess, handleWarning } = useErrorHandler(
    `Team: ${watchedName || `Team ${teamIndex + 1}`}`,
  );
  const { onSaveError, onMoveError } = useWriteErrors('team', handleError);

  // Use breakpoint detection to conditionally render only one DndContext
  // This prevents dnd-kit duplicate ID conflicts that break input fields
  const isDesktop = useMediaQuery('(min-width: 1280px)');

  // Query invalidation for refreshing data after import
  const invalidateSimulation = useInvalidateSimulation();

  const {
    fields: deliverableFields,
    remove: removeDeliverable,
    move: moveDeliverable,
    drag: deliverableDrag,
  } = useSyncedFieldArray({ control, name: `teams.${teamIndex}.deliverables` });

  const teamErrors = errors.teams?.[teamIndex];

  // useWatch is more performant than watch() - isolates subscriptions to specific fields
  const teams = useWatch({ control, name: 'teams' });

  // Rows are keyed by the deliverable's own id, not by useFieldArray's row key:
  // this list's path holds the team's position, and when another user moves
  // the team, react-hook-form issues new row keys for the new path whenever a
  // row holds unsaved text — which would remount every row and take the
  // user's caret with it (shipcast-nad4).
  // Read with getValues, which is current in the very render where teamIndex
  // changes; the watched `teams` can still hold the previous order there.
  const rowValues = getValues(`teams.${teamIndex}.deliverables`) ?? [];
  const rowKeys = deliverableFields.map((field, index) => rowValues[index]?.id ?? field.id);
  const features = useWatch({ control, name: `features` }) || [];
  const teamId = useWatch({ control, name: `teams.${teamIndex}.id` });

  // Watch values for auto-save (watchedName already declared above for error context)
  const watchedThroughput = useWatch({ control, name: `teams.${teamIndex}.throughput_data` });
  const throughputValues = useMemo(() => parseThroughputData(watchedThroughput ?? ''), [watchedThroughput]);
  const throughputSource = useTeamThroughputSource(teamId);
  const watchedScale = useWatch({ control, name: `teams.${teamIndex}.throughput_scale` });
  const watchedWip = useWatch({ control, name: `teams.${teamIndex}.wip` });
  const watchedColor = useWatch({ control, name: `teams.${teamIndex}.color` });

  // Get team color from stored value or fallback to index
  const teamColor = getTeamColor(teamIndex, watchedColor);

  const autoSaveFields = useMemo(
    () => teamAutoSaveFields(throughputSource.fromDataset),
    [throughputSource.fromDataset],
  );

  // Unified auto-save hook - replaces ~65 lines of manual logic
  const { status: saveStatus, rebase } = useAutoSave({
    values: {
      name: watchedName,
      throughput_data: watchedThroughput,
      throughput_scale: watchedScale,
      wip: watchedWip,
      color: watchedColor,
    },
    fields: autoSaveFields,
    // Cast to unknown because transforms change the types (throughput_data: string -> number[])
    saveFn: (payload) => (teamId ? updateTeam(teamId, payload as Record<string, unknown>) : Promise.resolve()),
    enabled: !!simulationId && !!teamId,
    onError: onSaveError,
  });

  // Values the cache sync writes into this team came from the server: they are
  // not edits, so auto-save must not send them back (shipcast-d8r.40).
  const onFieldSynced = useCallback(
    (path: string, value: unknown) => {
      for (const [field, synced] of syncedTeamFields(path, value, teamIndex)) rebase(field, synced);
    },
    [teamIndex, rebase],
  );
  useSyncedFields(onFieldSynced);

  // Get all available predecessors across all teams (UUID-based)
  const availablePredecessors: DeliverableOption[] = getAllDeliverableOptions(teams, features);

  // Reverse dependency map: for each deliverable, who lists it as a predecessor?
  //
  // This is the mirror of the "Blocked by" direction, which reaches the row as
  // stored data enriched by `getAllDeliverableOptions`. Nothing enriches this
  // one, so every field a chip renders has to be carried here explicitly —
  // `sourceId` included, or same-named slices of one epic become
  // indistinguishable in this direction only.
  const blockingByDeliverableId = useMemo(() => {
    const map: Record<string, BlockingDeliverable[]> = {};
    for (let ti = 0; ti < teams.length; ti++) {
      const team = teams[ti]!;
      const color = getTeamColor(ti, team.color);
      for (let di = 0; di < team.deliverables.length; di++) {
        const d = team.deliverables[di]!;
        if (!d.id) continue;
        for (const predId of d.predecessors) {
          if (!map[predId]) map[predId] = [];
          map[predId].push({
            id: d.id,
            teamName: team.name,
            teamColor: color,
            deliverableName: d.name,
            sourceId: d.source_id ?? null,
            priority: di + 1,
            teamIndex: ti,
            deliverableIndex: di,
          });
        }
      }
    }
    return map;
  }, [teams]);

  // Remove a blocking relationship: the blocking deliverable lists currentDeliverableId
  // as a predecessor, so we remove it from the blocker's predecessors array.
  // This is the inverse of removePredecessor in DeliverableForm.
  const handleRemoveBlocking = useCallback(
    (
      currentDeliverableId: string,
      blockerTeamIndex: number,
      blockerDeliverableIndex: number,
      blockerDeliverableId: string,
    ) => {
      const predecessorPath =
        `teams.${blockerTeamIndex}.deliverables.${blockerDeliverableIndex}.predecessors` as `teams.${number}.deliverables.${number}.predecessors`;
      const current = getValues(predecessorPath) || [];

      // Optimistic update: remove currentDeliverableId from the blocker's predecessors
      setValue(
        predecessorPath,
        current.filter((id) => id !== currentDeliverableId),
      );

      // Persist via API if simulation is saved
      if (simulationId && blockerDeliverableId && currentDeliverableId) {
        const blockerTeamId = getValues(`teams.${blockerTeamIndex}.id`);
        if (blockerTeamId) {
          deleteSpecificDependency(blockerDeliverableId, currentDeliverableId).catch((err) => {
            // Revert on error
            setValue(predecessorPath, current);
            handleError(err);
          });
        }
      }
    },
    [getValues, setValue, simulationId, handleError],
  );

  // Scroll to highlighted deliverable when it changes
  useEffect(() => {
    if (!highlightedDeliverableId || !isActive) return;

    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-deliverable-id="${CSS.escape(highlightedDeliverableId)}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [highlightedDeliverableId, isActive]);

  const handleRemoveDeliverable = async (index: number) => {
    const deliverable = getValues(`teams.${teamIndex}.deliverables.${index}`);

    // Auto-delete from database if saved
    if (simulationId && deliverable.id) {
      try {
        await deleteDeliverable(deliverable.id);
      } catch (err) {
        handleError(err);
        return; // Don't remove from form if delete failed
      }
    }

    removeDeliverable(index);
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const oldIndex = rowKeys.indexOf(String(active.id));
    const newIndex = over ? rowKeys.indexOf(String(over.id)) : -1;
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      deliverableDrag.ended(false);
      return;
    }

    // Update local form state (optimistic update)
    moveDeliverable(oldIndex, newIndex);
    deliverableDrag.ended(true);

    // Send the move itself, not this screen's whole list: the server applies
    // it to the current order, so another user's concurrent move survives.
    const ids = (getValues(`teams.${teamIndex}.deliverables`) || []).map((d) => d.id);
    const deliverableId = ids[newIndex];
    if (teamId && deliverableId) {
      try {
        await postDeliverableMove(teamId, deliverableId, anchorAbove(ids, newIndex));
      } catch (error) {
        onMoveError(error);
      }
    }
  };

  // Export team data to Excel
  const handleExportTeam = async () => {
    if (!teamId) {
      handleError('Team must be saved before exporting');
      return;
    }

    setIsExporting(true);
    try {
      const blob = await exportTeamSheet(teamId);
      const teamName = getValues(`teams.${teamIndex}.name`) || 'team';
      const timestamp = new Date().toISOString().slice(0, 16).replace('T', ' ').replace(/:/g, '-');
      downloadBlob(blob, `${teamName} ${timestamp}.xlsx`);
      handleSuccess('Team exported successfully');
    } catch (err) {
      handleError(err);
    } finally {
      setIsExporting(false);
    }
  };

  // Import team data from Excel (PUT - Replace All)
  const handleImportTeamReplace = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!teamId) {
      handleError('Team must be saved before importing');
      if (fileInputReplaceRef.current) {
        fileInputReplaceRef.current.value = '';
      }
      return;
    }

    setIsImportingReplace(true);
    try {
      await importTeamSheet(teamId, file);
      // Refresh the form with new data from server
      if (onRefresh) {
        await onRefresh();
      } else if (simulationId) {
        // Fallback to query invalidation if no refresh callback
        await invalidateSimulation(simulationId);
      }
      handleSuccess('Team data replaced successfully');
    } catch (err) {
      handleError(err);
    } finally {
      setIsImportingReplace(false);
      if (fileInputReplaceRef.current) {
        fileInputReplaceRef.current.value = '';
      }
    }
  };

  // Merge/update team data from Excel (PATCH - Merge)
  const handleImportTeamMerge = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!teamId) {
      handleError('Team must be saved before importing');
      if (fileInputMergeRef.current) {
        fileInputMergeRef.current.value = '';
      }
      return;
    }

    setIsImportingMerge(true);
    try {
      const result = await patchTeamSheet(teamId, file);
      // Refresh the form with new data from server
      if (onRefresh) {
        await onRefresh();
      } else if (simulationId) {
        // Fallback to query invalidation if no refresh callback
        await invalidateSimulation(simulationId);
      }
      const summary = [
        `${result.deliverables_created} created`,
        `${result.deliverables_updated} updated`,
        result.deliverables_deleted > 0 ? `${result.deliverables_deleted} deleted` : null,
      ]
        .filter(Boolean)
        .join(', ');
      handleSuccess(`Team data merged successfully (${summary})`);

      // Display consolidated warnings to the user
      if (result.warnings.length > 0) {
        // Group predecessor-related warnings
        const predecessorWarnings = result.warnings.filter((w) => w.includes('Predecessor'));
        const otherWarnings = result.warnings.filter((w) => !w.includes('Predecessor'));

        // Show consolidated predecessor warning
        if (predecessorWarnings.length > 0) {
          // Extract predecessor references from warning messages
          const predecessorRefs = predecessorWarnings
            .map((w) => {
              const match = w.match(/Predecessor '([^']+)'/);
              return match ? match[1] : null;
            })
            .filter(Boolean);

          if (predecessorRefs.length > 0) {
            const message =
              predecessorRefs.length === 1
                ? `Dependency to '${predecessorRefs[0]}' could not be resolved. Import the team containing this item and re-import to create the dependency.`
                : `${predecessorRefs.length} dependencies could not be resolved: ${predecessorRefs.join(', ')}. Import the teams containing these items and re-import to create the dependencies.`;
            handleWarning(message, { autoDismiss: false });
          }
        }

        // Show other warnings separately
        otherWarnings.forEach((warning) => {
          handleWarning(warning, { autoDismiss: false });
        });
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsImportingMerge(false);
      if (fileInputMergeRef.current) {
        fileInputMergeRef.current.value = '';
      }
    }
  };

  return (
    <div className="overflow-visible">
      {/* Team Header - Color icon + Name + Actions */}
      <div className="flex items-center gap-3 border-b border-border-default/50 px-6 py-4">
        {/* Color Picker - icon fully colored */}
        <ColorPicker
          value={teamColor}
          onChange={(color) => setValue(`teams.${teamIndex}.color`, color)}
          trigger={
            <button
              type="button"
              className="
                flex size-10 shrink-0 items-center justify-center rounded-xl transition-all
                hover:scale-105 hover:shadow-md
              "
              style={{ backgroundColor: teamColor }}
              title="Click to change team color"
            >
              <Users className="size-5 text-white drop-shadow-sm" />
            </button>
          }
        />

        {/* Editable team name */}
        <div className="min-w-0 flex-1">
          <input
            type="text"
            placeholder="Team Name"
            className="
              -mx-1 w-full rounded-sm border border-transparent bg-transparent px-1 text-lg font-semibold
              text-text-primary transition-colors
              placeholder:text-text-muted
              hover:border-border-strong
              focus:border-transparent focus:ring-1 focus:ring-primary-500 focus:outline-hidden
            "
            {...register(`teams.${teamIndex}.name`)}
          />
          {teamErrors?.name?.message && <p className="mt-0.5 text-xs text-feedback-error">{teamErrors.name.message}</p>}
          {/* Where this team's data comes from — a property of the team, read
              by both Deliverables and Throughput (shipcast-o7j Q1). */}
          <TeamDataSourcePicker
            teamId={teamId}
            // The other SAVED teams: a pre-save team reads no dataset yet.
            siblings={teams.flatMap((t) => (t.id && t.id !== teamId ? [{ id: t.id, name: t.name }] : []))}
          />
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-2">
          {/* Save Status */}
          <div className="flex items-center gap-1.5 text-xs text-text-muted">
            {saveStatus === 'saving' && (
              <>
                <Loader2 className="size-3.5 animate-spin text-action-primary" />
                <span
                  className="
                    hidden
                    sm:inline
                  "
                >
                  Saving...
                </span>
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <Check className="size-3.5 text-feedback-success" />
                <span
                  className="
                    hidden
                    sm:inline
                  "
                >
                  Saved
                </span>
              </>
            )}
            {saveStatus === 'error' && (
              <>
                <AlertCircle className="size-3.5 text-feedback-error" />
                <span
                  className="
                    hidden
                    sm:inline
                  "
                >
                  Error
                </span>
              </>
            )}
          </div>

          {/* Hidden file inputs */}
          <input
            type="file"
            ref={fileInputReplaceRef}
            onChange={handleImportTeamReplace}
            accept=".xlsx"
            className="hidden"
          />
          <input
            type="file"
            ref={fileInputMergeRef}
            onChange={handleImportTeamMerge}
            accept=".xlsx"
            className="hidden"
          />

          {/* Excel dropdown - only for saved teams */}
          {teamId && (
            <Dropdown
              trigger={
                <Button type="button" variant="secondary" size="sm">
                  {isExporting || isImportingReplace || isImportingMerge ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <>
                      <Download className="mr-1 size-4" />
                      <span
                        className="
                          hidden
                          sm:inline
                        "
                      >
                        Excel
                      </span>
                      <ChevronDown className="ml-1 size-3" />
                    </>
                  )}
                </Button>
              }
            >
              <Menu width="sm">
                <MenuItem icon={Download} onClick={handleExportTeam} disabled={isExporting}>
                  Export to Excel
                </MenuItem>
                <div className="border-t border-border-default/50" />
                <MenuItem
                  icon={Upload}
                  onClick={() => fileInputMergeRef.current?.click()}
                  disabled={isImportingReplace || isImportingMerge}
                >
                  Import & Merge
                </MenuItem>
                <MenuItem
                  icon={Upload}
                  onClick={() => fileInputReplaceRef.current?.click()}
                  disabled={isImportingReplace || isImportingMerge}
                >
                  Import & Replace All
                </MenuItem>
              </Menu>
            </Dropdown>
          )}

          {canRemove && (
            <IconButton type="button" variant="ghost-danger" size="sm" onClick={onRemove} aria-label="Delete team">
              <Trash2 className="size-4" />
            </IconButton>
          )}
        </div>
      </div>

      <div className="space-y-6 p-6">
        {/* Team Configuration - subtle inset with unified dark mode */}
        <div className="rounded-xl border border-border-default/20 bg-bg-muted/30 p-5 shadow-xs">
          <div
            className="
              flex flex-col items-start gap-4
              lg:flex-row lg:gap-5
            "
          >
            {/* Throughput History with icon */}
            <div
              className="
                w-full min-w-0 space-y-1.5
                lg:w-auto lg:flex-1
              "
            >
              <div className="flex items-center gap-1.5">
                <TrendingUp className="size-4 text-text-secondary" />
                <Label>Throughput History</Label>
                <div className="ml-auto">
                  {/* Refresh, range and cap; applying reloads the form's copy
                      of the team, as a deliverables refresh does. */}
                  <ThroughputSetup teamId={teamId} onApplied={onRefresh} />
                </div>
              </div>
              {/* A tracker-fed series is replaced on every refresh, so it is
                  shown, not typed (shipcast-o7j Q8). */}
              {throughputSource.fromDataset ? (
                teamErrors?.throughput_data?.message && (
                  <p role="alert" className="text-xs text-feedback-error">
                    {teamErrors.throughput_data.message}
                  </p>
                )
              ) : (
                <Input
                  placeholder="5, 6, 7, 8, 5, 4, 6, 7, 5, 6"
                  hint="Comma-separated daily throughput values from historical data"
                  error={teamErrors?.throughput_data?.message}
                  {...register(`teams.${teamIndex}.throughput_data`)}
                />
              )}
              <ThroughputCharts values={throughputValues} lastDay={throughputSource.lastDay} />
            </div>

            {/* Scale and WIP - side by side on all screen sizes */}
            <div
              className="
                flex gap-4
                lg:gap-5
              "
            >
              {/* Scale with icon */}
              <div className="w-35">
                <StepperInput
                  label="Scale"
                  icon={Zap}
                  value={Math.round((watchedScale || 1) * 100)}
                  onChange={(val) => methods.setValue(`teams.${teamIndex}.throughput_scale`, val / 100)}
                  min={10}
                  max={500}
                  step={1}
                  suffix="%"
                  hint="Throughput multiplier"
                />
              </div>

              {/* WIP with icon */}
              <div className="w-35">
                <StepperInput
                  label="WIP"
                  icon={Layers}
                  value={watchedWip || 1}
                  onChange={(val) => methods.setValue(`teams.${teamIndex}.wip`, val)}
                  min={1}
                  max={20}
                  step={1}
                  hint="Max deliverables"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Deliverables */}
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-medium text-text-primary">Deliverables</h4>
              <p className="text-xs text-text-muted">Drag to reorder priorities</p>
            </div>
            {/* Refresh from the team's dataset; a finished refresh reloads the
                form's copy of the team, as an Excel import does. */}
            <DeliverablesSetup teamId={teamId} onApplied={onRefresh} />
          </div>

          {isDesktop ? (
            /* Desktop table view - px-1 provides space for ring highlight to not be clipped */
            <div className="w-full overflow-x-auto px-1">
              <div className="min-w-275 overflow-visible">
                {/* Deliverable Rows with Drag and Drop */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={deliverableDrag.started}
                  onDragCancel={() => deliverableDrag.ended(false)}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={rowKeys} strategy={verticalListSortingStrategy}>
                    {/* Deliverable list - p-1 provides space for ring highlight */}
                    <div className="p-1">
                      {rowKeys.map((rowKey, deliverableIndex) => (
                        <div key={rowKey} className="pr-1 pb-1">
                          <DeliverableForm
                            mode="edit"
                            id={rowKey}
                            teamIndex={teamIndex}
                            teamId={teamId || ''}
                            deliverableIndex={deliverableIndex}
                            onRemove={() => handleRemoveDeliverable(deliverableIndex)}
                            canRemove={true}
                            availablePredecessors={availablePredecessors}
                            blockingDeliverables={
                              blockingByDeliverableId[teams[teamIndex]?.deliverables[deliverableIndex]?.id ?? ''] ?? []
                            }
                            onRemoveBlocking={handleRemoveBlocking}
                            availableFeatures={features}
                            simulationId={simulationId}
                            variant="table"
                            onDependencyClick={onDependencyClick}
                            highlightedDeliverableId={highlightedDeliverableId}
                          />
                        </div>
                      ))}
                      {/* Always-visible creation form at the end of the list */}
                      {teamId && (
                        <div className="pr-1 pb-1">
                          <DeliverableForm
                            mode="create"
                            teamId={teamId}
                            availableFeatures={features}
                            simulationId={simulationId}
                            variant="table"
                          />
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          ) : (
            /* Card view for smaller screens - conditionally rendered */
            <div className="space-y-3">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={deliverableDrag.started}
                onDragCancel={() => deliverableDrag.ended(false)}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={rowKeys} strategy={verticalListSortingStrategy}>
                  {rowKeys.map((rowKey, deliverableIndex) => (
                    <DeliverableForm
                      key={rowKey}
                      mode="edit"
                      id={rowKey}
                      teamIndex={teamIndex}
                      teamId={teamId || ''}
                      deliverableIndex={deliverableIndex}
                      onRemove={() => handleRemoveDeliverable(deliverableIndex)}
                      canRemove={true}
                      availablePredecessors={availablePredecessors}
                      blockingDeliverables={
                        blockingByDeliverableId[teams[teamIndex]?.deliverables[deliverableIndex]?.id ?? ''] ?? []
                      }
                      onRemoveBlocking={handleRemoveBlocking}
                      availableFeatures={features}
                      simulationId={simulationId}
                      variant="card"
                      onDependencyClick={onDependencyClick}
                      highlightedDeliverableId={highlightedDeliverableId}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {/* Always-visible creation form at the end of the list */}
              {teamId && (
                <DeliverableForm
                  mode="create"
                  teamId={teamId}
                  availableFeatures={features}
                  simulationId={simulationId}
                  variant="card"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
